import { useState, useLayoutEffect, useMemo } from 'react';
import {
  getActiveCart,
  CartEvents,
  CartEventsEnum,
  CartEventsVersionsEnum,
  CartConstantsEnum,
  updateCart,
  PromotionalItemOptions,
  getTransformedEntries,
  SelectedProducts,
  Product,
  getUniquePromotions,
  getFiredPromotionFromEntries,
  cartApi,
  Cart,
} from '@commerce-cart-webcomponents/sdk';
import { useNextgenRouter } from '@nextgen-web-framework/all';
import { SpecialOfferProducts, SopVersion } from '@commerce-promotion-webcomponents/ui';
import { useDispatch } from 'react-redux';
import { ConsoleLogger, StorageService } from '@nextgen-web-framework/core';
import { useFeatureFlag } from '../../../common';
import { SpecialOfferAlertProps, PromotionalEntries } from './types/propTypes';

const SpecialOfferAlert = ({ data }: SpecialOfferAlertProps) => {
  const router = useNextgenRouter();
  const dispatch = useDispatch();
  const cartId = String(router.params?.cartId);
  const mainSubCart = getActiveCart(data, cartId);
  const { entries: cartEntries, promotionalItemOptions } = mainSubCart;
  const { showAuxiliaryToggleFlag } = useFeatureFlag();

  const [selectedProducts, setSelectedProducts] = useState<SelectedProducts>({});

  const promotionalEntries = useMemo(() => {
    const entries: PromotionalEntries = {};
    cartEntries?.forEach((entry) => {
      const prmId = entry?.partnerPromotionId;
      if (prmId) {
        entries[prmId] = {
          ...(entries[prmId] ?? {}),
          [entry.product.id]: {
            quantity: entry.quantity,
            entryId: entry.entryId,
          },
        };
      }
    });
    return entries;
  }, [cartEntries]);

  useLayoutEffect(() => {
    const selectedProductsObj: SelectedProducts = {};
    promotionalItemOptions?.forEach((itemOption) => {
      if (promotionalEntries[String(itemOption.prmId)]) {
        matchProducts(selectedProductsObj, itemOption);
      }
    });
    setSelectedProducts(selectedProductsObj);
  }, [cartEntries]);

  const matchProducts = (selectedProductsObj: SelectedProducts, itemOption: PromotionalItemOptions) => {
    const { prmId = '', giftPromotion, promotionalItems } = itemOption;
    const itemSet = promotionalItems?.reduce((res, { itemSet = [] }) => [...res, ...itemSet], [] as Product[]) ?? [];
    for (const product of itemSet) {
      const prmProduct = promotionalEntries[prmId][product.id];
      if (prmProduct) {
        selectedProductsObj[prmId] = {
          ...(selectedProductsObj[prmId] ?? {}),
          [product.id]: {
            entryId: prmProduct.entryId,
            giftPromotion,
            quantity: prmProduct.quantity,
          },
        };
      }
    }
  };

  const updateCartEntries = async (payloadData: SelectedProducts, onSuccess: () => void, onError: () => void) => {
    const allEntries = getTransformedEntries(payloadData);

    const payload = {
      subCarts: [
        {
          subCartId: mainSubCart?.subCartId,
          entries: allEntries,
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    try {
      const res = await cartApi.updateCartEntries({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        payload,
      });
      dispatch(updateCart(res as Cart));
      onSuccess();
      CartEvents.publishEvent({
        event: CartEventsEnum.UpdateCartEntryQty,
        payload: {
          cart: res,
          cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        },
      });
    } catch (e) {
      ConsoleLogger.log('Error occured while updating subcarts');
      onError();
    }
  };

  const getAllFiredPromotion = () => {
    const { promotionResults, entries } = mainSubCart;
    return getUniquePromotions(
      [...(promotionResults?.firedPromotions ?? []), ...getFiredPromotionFromEntries(entries)],
      showAuxiliaryToggleFlag,
    );
  };

  return (
    <SpecialOfferProducts
      itemOptions={promotionalItemOptions}
      selectedProducts={selectedProducts}
      version={SopVersion.TWO}
      onMount={(openDrawer: () => void) =>
        CartEvents.subscribeEvents({
          topics: [CartEventsEnum.OpenPromotionDrawer],
          version: CartEventsVersionsEnum['1.0.0'],
          cb: openDrawer,
        })
      }
      onSave={(payLoad, onSuccess, onError) => updateCartEntries(payLoad, onSuccess, onError)}
      firedPromotions={getAllFiredPromotion()}
      shouldValidateSelection={true}
      minRequiredSelection={0}
    />
  );
};

export { SpecialOfferAlert };
